import { createContext, useState, useEffect, lazy, Suspense } from 'react';
import { useParams, useRouteLoaderData } from 'react-router-dom';
import { useGlobalState, setSavePopup, getSavePopup } from "../configurator/State";
import bmkservice from '../service/bmk-service';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { logoutUser } from '../store/slices/authSlice';
import { graphqlRequest } from '../service/graphql-client';
import { GQ_ME_QUERY, GQ_SAVE_BUILD } from '../service/graphql-mutations';
import { pushAuthStatus, trackBuildShared, trackBuildSaved, pushTotalPrice } from '../utils/analytics';

// Import modal components
import Modal from '../components/modals/Modal';
import LoginModal from '../components/modals/LoginModal';
import SignupModal from '../components/modals/SignupModal';
import ProfileModal from '../components/modals/ProfileModal';
import ForgotPasswordModal from '../components/modals/ForgotPasswordModal';
import SaveBuildModal from '../components/modals/SaveBuildModal';
import LoadBuildModal from '../components/modals/LoadBuildModal';
import ShareModal from '../components/modals/ShareModal';
import ShareByEmailModal from '../components/modals/ShareByEmailModal';
import DisclaimerModal from '../components/modals/DisclaimerModal';
import ErrorModal from '../components/modals/ErrorModal';
import LoadingModal from '../components/modals/LoadingModal';
import SuccessModal from '../components/modals/SuccessModal';
import ChangePasswordModal from '../components/modals/ChangePasswordModal';

const LazyGoogleOAuthProvider = lazy(() =>
    import('@react-oauth/google').then(m => ({ default: m.GoogleOAuthProvider }))
);

export const ModalContext = createContext();

export default function ModalProvider({ children }) {
    const dispatch = useAppDispatch();
    const { accessToken } = useAppSelector((state) => state.auth);
    const [modalType, setModalType] = useState(null);
    const [modalProps, setModalProps] = useState({});
    const productDetails = useRouteLoaderData("productDetails");
    const [user] = useGlobalState('User');
    const { productSlug, seriesSlug, modelName } = useParams();

    const [prices] = useGlobalState("Prices");
    const [kmaintenance] = useGlobalState("KMaintenance");
    const [warranty] = useGlobalState("Warranty");
    const [insurance] = useGlobalState("Insurance");
    const [ZipCode] = useGlobalState("ZipCode");

    const fullProductURL = window.location.protocol + "//" + window.location.host + '/' +
        encodeURIComponent(productSlug) + '/' + encodeURIComponent(seriesSlug) + '/' + encodeURIComponent(modelName);

    const closeModal = () => {
        setModalType(null);
        setModalProps({});
    };

    const show = (content) => {
        if (!content) {
            // If no content provided, close the modal
            closeModal();
            return;
        }
        // If content is a string, treat it as a loading message
        if (typeof content === 'string') {
            setModalType('loading');
            setModalProps({ message: content });
        } else {
            // Otherwise, treat it as custom JSX content
            setModalType('custom');
            setModalProps({ content });
        }
    };

    const showDisclaimer = (index) => {
        setModalType('disclaimer');
        setModalProps({ disclaimerIndex: index });
    };

    const trySave = () => {
        if (!productDetails || !productDetails.Tractor || !productDetails.Tractor.Material || productDetails.Tractor.Material.length <= 0) {
            setModalType('error');
            setModalProps({
                title: 'Finish Building',
                message: 'Please select and get started on your custom build out your Dream Vehicle before trying to Save.'
            });
        } else {
            showLogin("Log In to Save Your Build", () => showSave());
        }
    };
    const showLogin = async (promptMessage, successHandler) => {
        if (accessToken) {
            try {
                const data = await graphqlRequest(GQ_ME_QUERY, {}, accessToken);

                if (data.me) {
                    successHandler(); // Call the success handler directly
                    return;
                }
            } catch (error) {
                // Token is invalid, continue to show login modal
                console.log('Token validation failed, showing login modal');
            }
        }

        // If no token or token is invalid, show login modal
        setModalType('login');
        setModalProps({
            promptMessage,
            onSuccess: () => {
                // After successful login, show profile first
                showProfile();
            },
            onShowSignup: () => showSignup(() => {
                // After successful signup, show profile first
                showProfile();
            }),
            onShowForgotPassword: () => showForgotPassword()
        });
    };

    const showSignup = (successHandler) => {
        setModalType('signup');
        setModalProps({
            onSuccess: successHandler,
            onShowLogin: () => showLogin("Log In to Save Your Build", successHandler)
        });
    };

    const showProfile = () => {
        setModalType('profile');
        setModalProps({
            onShowSavedBuilds: () => showLoad(),
            onSaveBuild: () => showSave(),
            onChangePassword: () => showChangePassword(),
            onSignOut: () => handleLogout()
        });
    };

    const showChangePassword = () => {
        setModalType('changePassword');
        setModalProps({
            onClose: () => showProfile()
        });
    };

    const showForgotPassword = () => {
        setModalType('forgotPassword');
        setModalProps({
            onClose: () => showLogin("Log In to Save Your Build"),
            onShowLogin: () => showLogin("Log In to Save Your Build")
        });
    };

    const handleLogout = async () => {
        try {
            await dispatch(logoutUser());
            // Just close the modal after logout
            closeModal();
        } catch (error) {
            console.error('Logout error:', error);
            // Even if logout fails, close modal
            closeModal();
        }
    };

    const showSave = () => {
        setModalType('saveBuild');
        setModalProps({
            onSave: saveAndConfirmBuild,
            productDetails
        });
    };

    const tryLoad = () => {
        showLogin("Log In to View Your Builds", () => showLoad());
    };

    const showLoad = () => {
        setModalType('loadBuild');
        setModalProps({
            onShowProfile: () => showProfile()
        });
    };

    useEffect(() => {
        document.addEventListener('mouseout', e => {
            if (!e.toElement && !e.relatedTarget && getSavePopup()) {
                // trySave();
                setSavePopup(false);
            }
        });
    }, []);

    useEffect(() => {
        pushAuthStatus(!!accessToken);
    }, [accessToken]);

    // Modal handlers for share functionality
    const showShare = async () => {
        setModalType('loading');
        setModalProps({ message: 'Loading...' });

        const savedBuild = await saveBuild();
        const shareURL = savedBuild.productURL + '?build-id=' + savedBuild.id;

        trackBuildShared();

        setModalType('share');
        setModalProps({
            savedBuild,
            productDetails,
            onShareByEmail: () => showShareByEmail(),
            onCopyLink: () => navigator.clipboard.writeText(shareURL)
        });
    };

    const showShareByEmail = () => {
        setModalType('shareByEmail');
        setModalProps({
            onSubmit: async (email) => {
                await sendToIKQ(2, { email: email });
            }
        });
    };

    // Essential functions for saving and sharing builds

    const saveAndConfirmBuild = async (saveBuildName) => {
        setModalType('loading');
        setModalProps({ message: 'Saving...' });

        await saveBuild(saveBuildName);

        trackBuildSaved();

        setModalType('success');
        setModalProps({
            title: 'Build Saved Successfully',
            message: 'Your Build Has Been Successfully Saved!'
        });
    };

    const saveBuild = async (saveBuildName) => {
        setSavePopup(false);

        if (!saveBuildName || saveBuildName.length <= 0)
            saveBuildName = 'My Dream ' + productDetails.Tractor.Material;

        // Prepare configured items - ensure it's a JSON string
        let configuredItems = productDetails.configuration.configuredItems;
        if (typeof configuredItems !== 'string') {
            configuredItems = JSON.stringify(configuredItems);
        }
        // WordPress wp_unslash() strips one level of backslashes on save,
        // corrupting escaped quotes in JSON (e.g., 60\" → 60", breaking JSON.parse).
        // Double-escape so one level survives: \" → \\" → (wp_unslash) → \"
        configuredItems = configuredItems.replace(/\\/g, '\\\\');

        // Find matching image
        const modelSets = [productDetails.Tractor.Modelset];
        const defaultView = Object.keys(productDetails.studios)[0];
        Object.values(productDetails.configuration.configuredItems).forEach((item) => {
            if (item.Modelset) {
                modelSets.push(item.Modelset);
            }
        });
        const matchingImage = productDetails.imageMappingData.find((imd) => {
            if (!productDetails.studios[defaultView] || imd.studio !== productDetails.studios[defaultView].id) {
                return false;
            }
            if (imd.modelsets.length !== modelSets.length) {
                return false;
            }
            if (!imd.modelsets.every((ms) => modelSets.includes(ms))) {
                return false;
            }
            return true;
        });

        let imageUrl = '';
        if (matchingImage) {
            imageUrl = productDetails.baseUrl + 'images/' + encodeURIComponent(matchingImage.image);
        }
        else {
            imageUrl = productDetails.Tractor.ImagePath;
        }

        // Use GraphQL mutation to save build
        try {
            const data = await graphqlRequest(GQ_SAVE_BUILD, {
                productURL: fullProductURL,
                savedURL: window.location.href,
                configuredItems: configuredItems,
                buildName: saveBuildName,
                uc: user || '',
                image: imageUrl,
                un: user || undefined
            }, accessToken);

            if (data.saveBuild && data.saveBuild.build) {
                return data.saveBuild.build;
            } else {
                throw new Error('Failed to save build');
            }
        } catch (error) {
            console.error('Error saving build:', error);
            throw error;
        }
    }

    async function sendToIKQ(actionType = 1, customerData = {}) {
        let quote = productDetails.configuration.createQuoteRequest();
        quote.customer = {
            firstName: '',
            lastName: '',
            email: '',
            phone: '',
            comments: '',
            zip: ZipCode,
            selectedDealer: '',
            overSixteen: 'True',
            optInMarketing: 'False',

            ...customerData
        };
        quote.userAction = actionType;
        quote.ipAddress = '127.0.0.1';

        quote.cashCIRAmount = prices.totalPrice;
        pushTotalPrice(prices.totalPrice);
        quote.financeCIRAmount = prices.monthlyPayment;
        quote.orangePlusAmount = 0;
        quote.interestInKtac = insurance || false;
        quote.interestInKMaintenance = kmaintenance || false;
        quote.interestInWarranty = warranty || false;
        quote.selectedPackageId = productDetails.configuration.package?.id;

        // update price to the quote.tractor object with total price, This total price will show in quote email
        // quote email shows the price with tractor price + options -> price
        // so we need to subtract the options price from the total price
        if (quote.tractor) {
            let totalPrice = prices.totalPrice;
            if (quote.options && Array.isArray(quote.options)) {
                quote.options.forEach(opt => {
                    if (opt && typeof opt.price === 'number') {
                        totalPrice -= opt.price;
                    }
                });
            }
            quote.tractor.price = totalPrice;
        }

        let response = await fetch(bmkservice.IKQSaveData,
            {
                method: "POST",
                headers: { 'content-type': 'application/json' },
                body: JSON.stringify(quote)
            }
        );
        let json = await response.json();

        if (!json || !json.BMKQuoteId) {
            return false;
        }

        // Trigger Calance-side PDF generation and storage
        await fetch(`${bmkservice.IKQGeneratePDF}${json.BMKQuoteId}`);

        return json.BMKQuoteId;
    }

    const renderModal = () => {
        if (!modalType) return null;

        const commonProps = { onClose: closeModal };

        switch (modalType) {
            case 'disclaimer':
                return <DisclaimerModal {...commonProps} {...modalProps} />;
            case 'login':
                return (
                    <Suspense fallback={null}>
                        <LazyGoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}>
                            <LoginModal {...commonProps} {...modalProps} />
                        </LazyGoogleOAuthProvider>
                    </Suspense>
                );
            case 'signup':
                return <SignupModal {...commonProps} {...modalProps} />;
            case 'profile':
                return <ProfileModal {...commonProps} {...modalProps} />;
            case 'forgotPassword':
                return <ForgotPasswordModal {...commonProps} {...modalProps} />;
            case 'saveBuild':
                return <SaveBuildModal {...commonProps} {...modalProps} />;
            case 'loadBuild':
                return <LoadBuildModal {...commonProps} {...modalProps} />;
            case 'share':
                return <ShareModal {...commonProps} {...modalProps} />;
            case 'shareByEmail':
                return <ShareByEmailModal {...commonProps} {...modalProps} />;
            case 'loading':
                return <LoadingModal {...commonProps} {...modalProps} />;
            case 'success':
                return <SuccessModal {...commonProps} {...modalProps} />;
            case 'error':
                return <ErrorModal {...commonProps} {...modalProps} />;
            case 'changePassword':
                return <ChangePasswordModal {...commonProps} {...modalProps} />;
            case 'custom':
                return modalProps.content || null;
            default:
                return null;
        }
    };

    return (
        <ModalContext.Provider value={{
            showDisclaimer, trySave, tryLoad, showShare, sendToIKQ,
            showLogin, showForgotPassword, showSignup, showProfile,
            show, closeModal
        }}>
            {modalType ? <Modal onClose={closeModal}>{renderModal()}</Modal> : null}
            {children}
        </ModalContext.Provider>
    )
}
